import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

interface RecetaPublicData {
  id: number;
  nombrePaciente: string | null;
  medicamentos: string | null;
  fecha: string;
}

function getInitials(fullName: string | null): string {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  return parts.map(p => p.charAt(0).toUpperCase()).join('.') + '.';
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const RecetaPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [receta, setReceta] = useState<RecetaPublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReceta = async () => {
      try {
        const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api/v1';
        const url = API_URL.endsWith('/') ? `${API_URL}recetas/public/${token}` : `${API_URL}/recetas/public/${token}`;
        const res = await axios.get(url);
        const data = res.data?.data || res.data;
        setReceta(data);
      } catch (err) {
        console.error('Error al cargar receta:', err);
        setError('No se pudo cargar la receta');
      } finally {
        setLoading(false);
      }
    };
    fetchReceta();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando receta...</p>
        </div>
      </div>
    );
  }

  if (error || !receta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 px-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">📋</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Receta no encontrada</h1>
          <p className="text-gray-600">{error || 'La receta que buscas no existe o ha sido eliminada.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 px-4 py-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <h1 className="text-center text-xl font-bold text-gray-800 mb-1">Receta Médica</h1>
          <p className="text-center text-sm text-gray-500 mb-4">{formatDate(receta.fecha)}</p>

          {/* Patient initials */}
          <div className="bg-blue-50 rounded-xl p-4 mb-4">
            <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-1">Paciente</p>
            <p className="text-2xl font-bold text-blue-900 tracking-wider">
              {getInitials(receta.nombrePaciente)}
            </p>
          </div>
        </div>

        {/* Medications */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h2 className="text-lg font-bold text-gray-800">Medicamentos</h2>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-base text-gray-800 whitespace-pre-wrap leading-relaxed font-medium">
              {receta.medicamentos || 'Sin medicamentos registrados'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 mb-2">
          <p className="text-xs text-gray-400">
            Escanee el código QR de su receta impresa para ver esta información
          </p>
        </div>
      </div>
    </div>
  );
};

export default RecetaPublicPage;
